import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Construction, BarChart3, Phone, Eye, EyeOff, ShieldCheck, MessageCircle, KeyRound, Ghost, Upload, Timer, MousePointerClick, XCircle, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ROOM_DEFS = [
  { key: 'oceans13', name: 'Oceans 13 (Bank Heist)', desc: 'Up to 15 players · Rycom Mall' },
  { key: 'demolition', name: 'Demolition Abduction', desc: 'Up to 4 players · Rycom Mall' },
  { key: 'pirate', name: 'Pirate Ship', desc: 'Up to 8 players · Okinawa City Gate 2' },
  { key: 'sweet_escape', name: 'Bake Me Happy: The Kitchen Escape', desc: '2–6 players · Rycom Mall · Kitchen/baking theme' },
];

function Toggle({ on, onToggle, disabled, testId }) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      data-testid={testId}
      className={`relative w-14 h-7 rounded-full transition-colors duration-200 focus:outline-none shrink-0 ${on ? 'bg-brand' : 'bg-neutral-700'}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform duration-200 ${on ? 'translate-x-7' : 'translate-x-0'}`} />
    </button>
  );
}

export default function SiteSettings() {
  const [rooms, setRooms] = useState({});
  const [underConstruction, setUnderConstruction] = useState(false);
  const [visitStats, setVisitStats] = useState(null);
  const [vonageConfig, setVonageConfig] = useState({ configured: false, api_key: '', api_secret: '', originator: '' });
  const [vonageForm, setVonageForm] = useState({ api_key: '', api_secret: '', originator: '' });
  const [vonageSaving, setVonageSaving] = useState(false);
  const [vonageSaved, setVonageSaved] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  // Admin credentials change
  const [credForm, setCredForm] = useState({ current_password: '', new_email: '', new_password: '', confirm_password: '' });
  const [credSaving, setCredSaving] = useState(false);
  const [credMsg, setCredMsg] = useState(null);
  const [showCurPwd, setShowCurPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');

  // SMS Reply Links (FB Messenger, IG, WhatsApp)
  const [replyLinks, setReplyLinks] = useState({ fb_username: '', ig_username: '', wa_number: '' });
  const [replyLinksSaving, setReplyLinksSaving] = useState(false);
  const [replyLinksSaved, setReplyLinksSaved] = useState(false);

  // Money PIN
  const [pinForm, setPinForm] = useState({ current: '', next: '', confirm: '' });
  const [pinSaving, setPinSaving] = useState(false);
  const [pinMsg, setPinMsg] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/settings/rooms`, { withCredentials: true }),
      axios.get(`${API}/api/settings/site`, { withCredentials: true }),
      axios.get(`${API}/api/track/stats`, { withCredentials: true }),
      axios.get(`${API}/api/admin/vonage-config`, { withCredentials: true }),
      axios.get(`${API}/api/admin/sms/reply-links`, { withCredentials: true }),
    ]).then(([rRes, sRes, vRes, tRes, lRes]) => {
      setRooms(rRes.data);
      setUnderConstruction(sRes.data.under_construction);
      setVisitStats(vRes.data);
      setVonageConfig(tRes.data);
      setVonageForm(prev => ({ ...prev, originator: tRes.data.originator || '' }));
      setReplyLinks(lRes.data || { fb_username: '', ig_username: '', wa_number: '' });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const toggleRoom = async (key) => {
    setSaving(key);
    const next = !rooms[key];
    try {
      const { data } = await axios.put(`${API}/api/settings/rooms`, { [key]: next }, { withCredentials: true });
      setRooms(data);
    } catch {}
    setSaving('');
  };

  const toggleConstruction = async () => {
    setSaving('construction');
    const next = !underConstruction;
    try {
      const { data } = await axios.put(`${API}/api/settings/site`, { under_construction: next }, { withCredentials: true });
      setUnderConstruction(data.under_construction);
    } catch {}
    setSaving('');
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 flex justify-center">
        <Loader2 size={28} className="text-brand animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      <div>
        <h2 className="font-heading text-2xl tracking-wider uppercase text-white mb-1">Site Settings</h2>
        <p className="font-sans text-sm text-neutral-400">Control which features are visible on the public website.</p>
      </div>

      {/* Under Construction */}
      <div className="border-2 border-yellow-600/40 bg-yellow-500/5 rounded-xl p-5" data-testid="under-construction-section">
        <div className="flex items-center gap-2 mb-4">
          <Construction size={18} className="text-yellow-500" />
          <h3 className="font-heading text-sm tracking-widest uppercase text-yellow-500">Maintenance Mode</h3>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <p className="font-heading text-base tracking-wide uppercase text-white">Website Under Construction</p>
            <p className="font-sans text-xs text-neutral-500 mt-1">
              When enabled, visitors will see a "Coming Soon / Under Construction" page instead of the normal site. Admin panel remains accessible.
            </p>
          </div>
          <Toggle
            on={underConstruction}
            onToggle={toggleConstruction}
            disabled={saving === 'construction'}
            testId="under-construction-toggle-btn"
          />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className={`inline-block w-2 h-2 rounded-full ${underConstruction ? 'bg-yellow-500' : 'bg-neutral-600'}`} />
          <span className="font-sans text-xs text-neutral-400">
            {underConstruction ? 'Site is showing construction page' : 'Site is live'}
          </span>
        </div>
      </div>

      {/* Halloween settings moved to the dedicated Haunted House admin tab */}
      <div className="border-2 border-orange-600/40 bg-gradient-to-br from-orange-950/30 to-black rounded-xl p-5" data-testid="halloween-moved">
        <p className="font-heading text-sm tracking-widest uppercase text-orange-500">Halloween Banner & Ticket System</p>
        <p className="font-sans text-xs text-neutral-400 mt-2">All Halloween banner controls (title, subtitle, GIFs, jump-scare sliders, ticket theme, disclaimer, live-actors badge, analytics) now live in the <span className="text-orange-400 font-heading tracking-widest">Haunted House</span> admin tab, alongside the QR-code ticketing system.</p>
        <a href="#" onClick={e => { e.preventDefault(); const btn = document.querySelector('[data-testid="tab-haunted"]'); btn && btn.click(); }} className="mt-3 inline-block font-heading text-xs tracking-widest uppercase text-orange-400 hover:text-orange-300 underline underline-offset-4">Open Haunted House tab →</a>
      </div>


      {/* Visit & Booking Tracker */}
      {visitStats && (
        <div className="border border-neutral-800 rounded-xl p-5 bg-neutral-900/50" data-testid="visit-tracker-section">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={18} className="text-brand" />
            <h3 className="font-heading text-sm tracking-widest uppercase text-neutral-300">Site Analytics</h3>
          </div>
          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <div className="border border-neutral-800 rounded-xl p-4 text-center bg-black/30" data-testid="visits-today">
              <p className="font-heading text-3xl text-white">{visitStats.today_visits}</p>
              <p className="font-heading text-[11px] tracking-widest uppercase text-neutral-500 mt-1">Visits Today</p>
            </div>
            <div className="border border-neutral-800 rounded-xl p-4 text-center bg-black/30" data-testid="visits-total">
              <p className="font-heading text-3xl text-white">{visitStats.total_visits}</p>
              <p className="font-heading text-[11px] tracking-widest uppercase text-neutral-500 mt-1">Total Visits</p>
            </div>
            <div className="border border-brand/30 rounded-xl p-4 text-center bg-brand/5" data-testid="bookings-today">
              <p className="font-heading text-3xl text-brand">{visitStats.today_bookings}</p>
              <p className="font-heading text-[11px] tracking-widest uppercase text-neutral-500 mt-1">Book Now Today</p>
            </div>
            <div className="border border-brand/30 rounded-xl p-4 text-center bg-brand/5" data-testid="bookings-total">
              <p className="font-heading text-3xl text-brand">{visitStats.total_bookings}</p>
              <p className="font-heading text-[11px] tracking-widest uppercase text-neutral-500 mt-1">Total Book Now</p>
            </div>
          </div>
          {/* Bar chart — last 14 days */}
          <div>
            <p className="font-heading text-[11px] tracking-widest uppercase text-neutral-600 mb-3">Last 14 Days</p>
            <div className="flex items-end gap-1 h-28">
              {visitStats.days.slice(0, 14).reverse().map(d => {
                const maxV = Math.max(...visitStats.days.slice(0, 14).map(x => x.visits), 1);
                const hV = Math.max((d.visits / maxV) * 100, 4);
                const maxB = Math.max(...visitStats.days.slice(0, 14).map(x => x.bookings), 1);
                const hB = d.bookings > 0 ? Math.max((d.bookings / maxB) * 100, 4) : 0;
                return (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-0.5 group relative">
                    <div className="w-full flex flex-col items-stretch gap-px" style={{ height: '100%', justifyContent: 'flex-end' }}>
                      {hB > 0 && <div className="w-full rounded-t bg-brand" style={{ height: `${hB * 0.4}%`, minHeight: '2px' }} />}
                      <div className="w-full rounded-t bg-neutral-600" style={{ height: `${hV}%` }} />
                    </div>
                    <span className="font-sans text-[9px] text-neutral-600 hidden sm:block">{d.date.slice(5)}</span>
                    <div className="absolute bottom-full mb-1 hidden group-hover:block bg-neutral-800 border border-neutral-700 rounded px-2 py-1 text-[10px] text-white font-sans whitespace-nowrap z-10">
                      {d.date}: {d.visits} visit{d.visits !== 1 ? 's' : ''}, {d.bookings} booking{d.bookings !== 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-neutral-600" /><span className="font-sans text-[10px] text-neutral-600">Visits</span></div>
              <div className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-brand" /><span className="font-sans text-[10px] text-neutral-600">Book Now</span></div>
            </div>
          </div>
          <p className="font-sans text-[10px] text-neutral-700 mt-3">Visits count unique IPs per day. Book Now counts every click.</p>
        </div>
      )}

      {/* SMS Reply Links — Facebook Messenger / IG / WhatsApp */}
      <div className="border border-neutral-800 rounded-xl p-5 bg-neutral-900/50" data-testid="reply-links-section">
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle size={18} className="text-brand" />
          <h3 className="font-heading text-sm tracking-widest uppercase text-neutral-300">SMS Reply Channels</h3>
        </div>
        <p className="font-sans text-xs text-neutral-500 mb-4">
          Customers can't reply directly to texts sent from an alphanumeric sender like "Oki-Escape". Save your social handles here, then use the <strong>Insert Reply Link</strong> buttons on the SMS form to drop a tap-to-open link into any message.
        </p>
        <div className="space-y-3">
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-blue-500" /> Facebook Messenger Page Username
            </label>
            <div className="flex items-center bg-black border border-neutral-700 rounded-lg focus-within:border-brand transition-colors">
              <span className="font-mono text-xs text-neutral-500 pl-3 pr-1 select-none">m.me/</span>
              <input
                type="text"
                value={replyLinks.fb_username}
                onChange={e => setReplyLinks(f => ({ ...f, fb_username: e.target.value }))}
                placeholder="okiescape"
                data-testid="reply-fb-input"
                className="flex-1 bg-transparent px-1 py-2.5 font-mono text-sm text-white focus:outline-none placeholder-neutral-600"
              />
            </div>
            {replyLinks.fb_username && (
              <p className="font-sans text-[10px] text-neutral-600 mt-1">
                Preview: <a href={`https://m.me/${replyLinks.fb_username}`} target="_blank" rel="noopener noreferrer" className="text-brand hover:underline font-mono">https://m.me/{replyLinks.fb_username}</a>
              </p>
            )}
            <p className="font-sans text-[10px] text-neutral-600 mt-1">Find this in Facebook → your Page → Settings → Page Setup → Username. Paste just the handle or the full m.me/facebook.com URL — we'll clean it.</p>
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-pink-500" /> Instagram Handle (optional)
            </label>
            <div className="flex items-center bg-black border border-neutral-700 rounded-lg focus-within:border-brand transition-colors">
              <span className="font-mono text-xs text-neutral-500 pl-3 pr-1 select-none">ig.me/m/</span>
              <input
                type="text"
                value={replyLinks.ig_username}
                onChange={e => setReplyLinks(f => ({ ...f, ig_username: e.target.value }))}
                placeholder="okiescape"
                data-testid="reply-ig-input"
                className="flex-1 bg-transparent px-1 py-2.5 font-mono text-sm text-white focus:outline-none placeholder-neutral-600"
              />
            </div>
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500" /> WhatsApp Number (optional)
            </label>
            <div className="flex items-center bg-black border border-neutral-700 rounded-lg focus-within:border-brand transition-colors">
              <span className="font-mono text-xs text-neutral-500 pl-3 pr-1 select-none">wa.me/</span>
              <input
                type="text"
                value={replyLinks.wa_number}
                onChange={e => setReplyLinks(f => ({ ...f, wa_number: e.target.value }))}
                placeholder="819068610100"
                data-testid="reply-wa-input"
                className="flex-1 bg-transparent px-1 py-2.5 font-mono text-sm text-white focus:outline-none placeholder-neutral-600"
              />
            </div>
            <p className="font-sans text-[10px] text-neutral-600 mt-1">Digits only, with country code (no + or spaces).</p>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={async () => {
                setReplyLinksSaving(true);
                const { data } = await axios.put(`${API}/api/admin/sms/reply-links`, replyLinks, { withCredentials: true });
                setReplyLinks(data);
                setReplyLinksSaving(false);
                setReplyLinksSaved(true);
                setTimeout(() => setReplyLinksSaved(false), 2500);
              }}
              disabled={replyLinksSaving}
              data-testid="reply-links-save-btn"
              className="font-heading text-xs tracking-widest uppercase bg-brand text-white px-5 py-2.5 rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50"
            >
              {replyLinksSaving ? 'Saving...' : 'Save Reply Channels'}
            </button>
            {replyLinksSaved && <span className="font-sans text-xs text-green-400">Saved!</span>}
          </div>
        </div>
      </div>

      {/* Vonage SMS Configuration */}
      <div className="border border-neutral-800 rounded-xl p-5 bg-neutral-900/50" data-testid="vonage-config-section">
        <div className="flex items-center gap-2 mb-4">
          <Phone size={18} className="text-brand" />
          <h3 className="font-heading text-sm tracking-widest uppercase text-neutral-300">Vonage SMS</h3>
          <span className={`font-heading text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full ${vonageConfig.configured ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-500'}`}>
            {vonageConfig.configured ? 'Connected' : 'Not Set Up'}
          </span>
        </div>
        <p className="font-sans text-xs text-neutral-500 mb-4">
          Enter your Vonage API Key + API Secret + Sender ID. Get them from <a href="https://dashboard.nexmo.com/getting-started/sms" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">dashboard.nexmo.com</a> → API Settings. Sender ID can be a registered phone number or an alphanumeric label (max 11 chars, e.g. "OkieEscape").
        </p>
        <div className="space-y-3">
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">API Key</label>
            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={vonageForm.api_key}
                onChange={e => setVonageForm(f => ({ ...f, api_key: e.target.value }))}
                placeholder={vonageConfig.api_key || 'a1b2c3d4'}
                data-testid="vonage-key-input"
                className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2.5 pr-10 font-mono text-sm text-white focus:border-brand focus:outline-none placeholder-neutral-600"
              />
              <button onClick={() => setShowKey(!showKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white transition-colors">
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">API Secret</label>
            <div className="relative">
              <input
                type={showSecret ? 'text' : 'password'}
                value={vonageForm.api_secret}
                onChange={e => setVonageForm(f => ({ ...f, api_secret: e.target.value }))}
                placeholder={vonageConfig.api_secret || '••••••••••••••••'}
                data-testid="vonage-secret-input"
                className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2.5 pr-10 font-mono text-sm text-white focus:border-brand focus:outline-none placeholder-neutral-600"
              />
              <button onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white transition-colors">
                {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Sender ID / From</label>
            <input
              type="text"
              value={vonageForm.originator}
              onChange={e => setVonageForm(f => ({ ...f, originator: e.target.value }))}
              placeholder="OkieEscape or +1234567890"
              data-testid="vonage-originator-input"
              maxLength={16}
              className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2.5 font-mono text-sm text-white focus:border-brand focus:outline-none placeholder-neutral-600"
            />
            <p className="font-sans text-[10px] text-neutral-600 mt-1">Note: Carriers in JP and US may overwrite alphanumeric sender IDs with a network-assigned number to ensure delivery.</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={async () => {
                setVonageSaving(true);
                const payload = {};
                if (vonageForm.api_key) payload.api_key = vonageForm.api_key;
                if (vonageForm.api_secret) payload.api_secret = vonageForm.api_secret;
                if (vonageForm.originator !== undefined) payload.originator = vonageForm.originator;
                const { data } = await axios.put(`${API}/api/admin/vonage-config`, payload, { withCredentials: true });
                setVonageConfig(data);
                setVonageSaving(false);
                setVonageSaved(true);
                setTimeout(() => setVonageSaved(false), 3000);
              }}
              disabled={vonageSaving}
              data-testid="vonage-save-btn"
              className="font-heading text-xs tracking-widest uppercase bg-brand text-white px-5 py-2.5 rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50"
            >
              {vonageSaving ? 'Saving...' : 'Save Credentials'}
            </button>
            {vonageSaved && <span className="font-sans text-xs text-green-400">Saved!</span>}
          </div>

          {/* DLR Webhook URL */}
          <div className="border-t border-neutral-800 pt-3 mt-2" data-testid="vonage-dlr-url-section">
            <p className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1">Delivery Receipt URL — paste in Vonage Dashboard</p>
            <code className="font-mono text-xs text-brand break-all">{API}/api/webhooks/vonage-dlr</code>
            <p className="font-sans text-[10px] text-neutral-600 mt-2">
              In Vonage Dashboard → <strong>Settings → SMS settings → Delivery receipts URL</strong> → paste this URL and save.
              Each SMS will then show its real carrier status (Delivered / Failed / Expired / Rejected) in the Message History.
            </p>
          </div>
        </div>
      </div>

      {/* Money PIN */}
      <div className="border border-neutral-800 rounded-xl p-5 bg-neutral-900/50" data-testid="money-pin-section">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound size={18} className="text-yellow-500" />
          <h3 className="font-heading text-sm tracking-widest uppercase text-neutral-300">Money PIN</h3>
        </div>
        <p className="font-sans text-xs text-neutral-500 mb-4">
          Required to view revenue (Square tab) and the day-revenue strip in Team Tasks. Default PIN is <strong>2026</strong> until changed. Must be exactly 4 digits.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Current PIN</label>
            <input type="password" inputMode="numeric" maxLength={4} value={pinForm.current} onChange={e => setPinForm(f => ({ ...f, current: e.target.value.replace(/\D/g, '') }))} placeholder="••••" data-testid="money-pin-current" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2.5 font-mono text-sm text-center tracking-[0.4em] text-white focus:border-brand focus:outline-none" />
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">New PIN</label>
            <input type="password" inputMode="numeric" maxLength={4} value={pinForm.next} onChange={e => setPinForm(f => ({ ...f, next: e.target.value.replace(/\D/g, '') }))} placeholder="••••" data-testid="money-pin-new" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2.5 font-mono text-sm text-center tracking-[0.4em] text-white focus:border-brand focus:outline-none" />
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Confirm</label>
            <input type="password" inputMode="numeric" maxLength={4} value={pinForm.confirm} onChange={e => setPinForm(f => ({ ...f, confirm: e.target.value.replace(/\D/g, '') }))} placeholder="••••" data-testid="money-pin-confirm" className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2.5 font-mono text-sm text-center tracking-[0.4em] text-white focus:border-brand focus:outline-none" />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-3">
          <button
            onClick={async () => {
              setPinMsg(null);
              if (pinForm.next.length !== 4) { setPinMsg({ ok: false, text: 'New PIN must be 4 digits' }); return; }
              if (pinForm.next !== pinForm.confirm) { setPinMsg({ ok: false, text: 'New PIN and confirmation do not match' }); return; }
              setPinSaving(true);
              try {
                await axios.put(`${API}/api/admin/money-pin`, { current_pin: pinForm.current, new_pin: pinForm.next }, { withCredentials: true });
                sessionStorage.removeItem('oe_money_stamp');
                sessionStorage.removeItem('oe_money_stamp_ts');
                setPinForm({ current: '', next: '', confirm: '' });
                setPinMsg({ ok: true, text: 'Money PIN updated. You\'ll need to re-enter the new PIN next time you view money.' });
              } catch (err) {
                setPinMsg({ ok: false, text: err.response?.data?.detail || 'Update failed' });
              }
              setPinSaving(false);
            }}
            disabled={pinSaving}
            data-testid="money-pin-save-btn"
            className="font-heading text-xs tracking-widest uppercase bg-yellow-500 text-black px-5 py-2.5 rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50"
          >
            {pinSaving ? 'Updating...' : 'Update PIN'}
          </button>
          {pinMsg && <span className={`font-sans text-xs ${pinMsg.ok ? 'text-green-400' : 'text-red-400'}`}>{pinMsg.text}</span>}
        </div>
      </div>

      {/* Change Admin Login */}
      <div className="border border-neutral-800 rounded-xl p-5 bg-neutral-900/50" data-testid="admin-credentials-section">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={18} className="text-brand" />
          <h3 className="font-heading text-sm tracking-widest uppercase text-neutral-300">Change Admin Login</h3>
        </div>
        <p className="font-sans text-xs text-neutral-500 mb-4">
          Update the admin email and/or password. You'll need to log in again with the new credentials next time on devices where "Save email & password" wasn't checked. Leave fields blank to keep the current value.
        </p>
        <div className="space-y-3">
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Current Password (required)</label>
            <div className="relative">
              <input
                type={showCurPwd ? 'text' : 'password'}
                value={credForm.current_password}
                onChange={e => setCredForm(f => ({ ...f, current_password: e.target.value }))}
                placeholder="Enter your current password to authorize"
                data-testid="admin-current-pwd-input"
                autoComplete="current-password"
                className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2.5 pr-10 font-mono text-sm text-white focus:border-brand focus:outline-none placeholder-neutral-600"
              />
              <button type="button" onClick={() => setShowCurPwd(!showCurPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white transition-colors">
                {showCurPwd ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">New Email</label>
            <input
              type="email"
              value={credForm.new_email}
              onChange={e => setCredForm(f => ({ ...f, new_email: e.target.value }))}
              placeholder="new-admin@okiescape.com (leave blank to keep current)"
              data-testid="admin-new-email-input"
              autoComplete="off"
              className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2.5 font-mono text-sm text-white focus:border-brand focus:outline-none placeholder-neutral-600"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">New Password</label>
              <div className="relative">
                <input
                  type={showNewPwd ? 'text' : 'password'}
                  value={credForm.new_password}
                  onChange={e => setCredForm(f => ({ ...f, new_password: e.target.value }))}
                  placeholder="At least 6 chars"
                  data-testid="admin-new-pwd-input"
                  autoComplete="new-password"
                  className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2.5 pr-10 font-mono text-sm text-white focus:border-brand focus:outline-none placeholder-neutral-600"
                />
                <button type="button" onClick={() => setShowNewPwd(!showNewPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white transition-colors">
                  {showNewPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div>
              <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Confirm New Password</label>
              <input
                type={showNewPwd ? 'text' : 'password'}
                value={credForm.confirm_password}
                onChange={e => setCredForm(f => ({ ...f, confirm_password: e.target.value }))}
                placeholder="Re-type the new password"
                data-testid="admin-confirm-pwd-input"
                autoComplete="new-password"
                className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2.5 font-mono text-sm text-white focus:border-brand focus:outline-none placeholder-neutral-600"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={async () => {
                setCredMsg(null);
                if (!credForm.current_password) { setCredMsg({ ok: false, text: 'Enter your current password' }); return; }
                if (!credForm.new_email && !credForm.new_password) { setCredMsg({ ok: false, text: 'Provide a new email and/or new password' }); return; }
                if (credForm.new_password && credForm.new_password !== credForm.confirm_password) { setCredMsg({ ok: false, text: 'New password and confirmation do not match' }); return; }
                setCredSaving(true);
                try {
                  const payload = { current_password: credForm.current_password };
                  if (credForm.new_email) payload.new_email = credForm.new_email;
                  if (credForm.new_password) payload.new_password = credForm.new_password;
                  const { data } = await axios.put(`${API}/api/admin/credentials`, payload, { withCredentials: true });
                  // Clear remembered creds so next login uses the new ones
                  if (credForm.new_email || credForm.new_password) {
                    localStorage.removeItem('oe_remember_email');
                    localStorage.removeItem('oe_remember_pwd');
                  }
                  setCredMsg({ ok: true, text: `Updated. New email: ${data.email}${data.password_changed ? ' · password changed' : ''}` });
                  setCredForm({ current_password: '', new_email: '', new_password: '', confirm_password: '' });
                } catch (err) {
                  setCredMsg({ ok: false, text: err.response?.data?.detail || 'Update failed' });
                }
                setCredSaving(false);
              }}
              disabled={credSaving}
              data-testid="admin-credentials-save-btn"
              className="font-heading text-xs tracking-widest uppercase bg-brand text-white px-5 py-2.5 rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50"
            >
              {credSaving ? 'Updating...' : 'Update Admin Login'}
            </button>
            {credMsg && (
              <span data-testid="admin-credentials-msg" className={`font-sans text-xs ${credMsg.ok ? 'text-green-400' : 'text-red-400'}`}>{credMsg.text}</span>
            )}
          </div>
        </div>
      </div>

      {/* Room Visibility */}
      <div className="border border-neutral-800 rounded-xl p-5 bg-neutral-900/50" data-testid="room-visibility-section">
        <h3 className="font-heading text-sm tracking-widest uppercase text-neutral-300 mb-5">Room Visibility</h3>
        <div className="space-y-5">
          {ROOM_DEFS.map(room => (
            <div key={room.key} className="flex items-center justify-between gap-4" data-testid={`room-toggle-row-${room.key}`}>
              <div className="flex-1 min-w-0">
                <p className="font-heading text-base tracking-wide uppercase text-white">{room.name}</p>
                <p className="font-sans text-xs text-neutral-500 mt-0.5">{room.desc}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className={`inline-block w-2 h-2 rounded-full ${rooms[room.key] ? 'bg-green-500' : 'bg-neutral-600'}`} />
                <Toggle
                  on={!!rooms[room.key]}
                  onToggle={() => toggleRoom(room.key)}
                  disabled={saving === room.key}
                  testId={`room-toggle-btn-${room.key}`}
                />
              </div>
            </div>
          ))}
        </div>
        <p className="font-sans text-xs text-neutral-600 mt-5">
          Hidden rooms won't appear on the homepage, directions, or FAQ pages. They remain in the Team Tasks reservation tracker.
        </p>
      </div>
    </div>
  );
}
